import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Table2, ExternalLink, Link2, Settings2, Info, CheckCircle2, FileSpreadsheet, Lock, Bell, Code, Copy, CheckCheck, Globe, Download, Clock, ShieldCheck, Pill, UserCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

const GoogleSheetsGuide = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [remiCopied, setRemiCopied] = useState(false);
  
  const websiteIdParam = searchParams.get('website_id') || "MASUKKAN_WEBSITE_ID_ANDA";
  const serviceAccountEmail = "service-account@chatbaze-widget.iam.gserviceaccount.com";
  
  // Link Template
  const templateLink = "https://docs.google.com/spreadsheets/d/1ipAezW5T-IoKCx4NKqgO7I8kYiAMOLGyMBRKQ06NRQU/edit?usp=sharing";
  const medicalTemplateLink = "https://docs.google.com/spreadsheets/d/1i4HtYqwL8_hwPXYaB0OYWjNBchzMROUjgQO2dh9yNHI/edit?usp=sharing";

  const fullAppsScript = `/**
 * SCRIPT OTOMATISASI NOTIFIKASI PELANGGAN (PERCETAKAN)
 */
const API_URL = "${window.location.origin}"; 
const WEBSITE_ID = "${websiteIdParam}";

function onEdit(e) {
  if (!e.value) return;
  const sheet = e.source.getActiveSheet();
  const range = e.range;
  const col = range.getColumn();
  const row = range.getRow();
  const newValue = e.value;
  
  const headers = sheet.getRange(1, 1, 1, sheet.getLastColumn()).getValues()[0];
  const statusColIdx = headers.indexOf("Status") + 1;
  const phoneColIdx = headers.indexOf("No WhatsApp") + 1;

  if (statusColIdx > 0 && col === statusColIdx && newValue === "Selesai") {
    const rowData = sheet.getRange(row, 1, 1, sheet.getLastColumn()).getValues()[0];
    const customerPhone = rowData[phoneColIdx - 1];
    const message = "Halo, Pesanan Anda sudah *SELESAI*!";
    sendToChatbaze(customerPhone, message);
  }
}

function sendToChatbaze(phone, message) {
  const payload = { website_id: WEBSITE_ID, phone: phone.toString().replace(/\\D/g, ''), message: message, secret: "chatbaze_secret_123" };
  const options = {
    method: "post",
    contentType: "application/json",
    payload: JSON.stringify(payload),
    muteHttpExceptions: true
  };
  try {
    const response = UrlFetchApp.fetch(API_URL + "/api/webhooks/google-sheets", options);
    if (response.getResponseCode() === 403) {
      // Konektor dinonaktifkan di Chatbaze, hapus trigger otomatis agar Apps Script berhenti total
      deleteTriggers();
    }
  } catch(e) {
    // Abaikan error koneksi sementara
  }
}

function deleteTriggers() {
  const triggers = ScriptApp.getProjectTriggers();
  for (let i = 0; i < triggers.length; i++) {
    ScriptApp.deleteTrigger(triggers[i]);
  }
}`;

  const remiAppsScript = `/**
 * SCRIPT REMINDER OBAT (MULTI-SHEET)
 */
const CONFIG = {
  API_URL: "https://api.chatbaze.id/api/webhooks/google-sheets",
  SECRET: "chatbaze_secret_123",
  WEBSITE_ID: "${websiteIdParam}", 
  TIMEZONE: "GMT+7"
};

function runMedicationReminder() {
  // Cek keaktifan koneksi secara instan (agar mati langsung di menit berikutnya jika dinonaktifkan)
  if (!checkConnectionActive()) {
    deleteTriggers();
    return;
  }

  const ss = SpreadsheetApp.getActiveSpreadsheet();
  const sheets = ss.getSheets(); 
  
  const now = new Date();
  const currentTime = Utilities.formatDate(now, CONFIG.TIMEZONE, "HH:mm"); 
  
  sheets.forEach(sheet => {
    const data = sheet.getDataRange().getValues();
    for (let i = 1; i < data.length; i++) {
      const [baris, nama, phone, obat, jadwal, status, keterangan] = data[i];
      if (!phone || !jadwal) continue;

      let formatJadwal = jadwal;
      if (jadwal instanceof Date) {
        formatJadwal = Utilities.formatDate(jadwal, CONFIG.TIMEZONE, "HH:mm");
      } else {
        const match = String(jadwal).match(/(\\d{1,2})[:\\.](\\d{2})/);
        if (match) {
          let h = parseInt(match[1]);
          const m = match[2];
          if (/pm/i.test(String(jadwal)) && h < 12) h += 12;
          if (/am/i.test(String(jadwal)) && h === 12) h = 0;
          formatJadwal = ("0" + h).slice(-2) + ":" + m;
        } else {
          formatJadwal = String(jadwal).trim();
        }
      }

      if (formatJadwal === currentTime && status !== "Sent") {
        const message = "*PENGINGAT MINUM OBAT*\\n\\nHalo *" + nama + "*,\\nSaatnya minum obat: *" + obat + "*\\nJam: " + formatJadwal + "\\nKeterangan: " + (keterangan || "-") + "\\n\\nSemoga lekas sembuh! 🙏";
        const success = callChatbazeAPI(phone, message, nama);
        if (success) sheet.getRange(i + 1, 6).setValue("Sent");
      }
    }
  });
}

function callChatbazeAPI(recipientPhone, message, visitorName) {
  const payload = {
    secret: CONFIG.SECRET,
    website_id: CONFIG.WEBSITE_ID,
    phone: String(recipientPhone).replace(/\\D/g, ""), 
    message: message,
    name: visitorName 
  };
  const options = {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify(payload),
    muteHttpExceptions: true
  };
  try {
    const response = UrlFetchApp.fetch(CONFIG.API_URL, options);
    const code = response.getResponseCode();
    if (code === 403) {
      // Konektor dinonaktifkan di Chatbaze, hapus trigger otomatis agar Apps Script berhenti total
      deleteTriggers();
      return false;
    }
    return code === 200;
  } catch(e) {
    return false;
  }
}

function deleteTriggers() {
  const triggers = ScriptApp.getProjectTriggers();
  for (let i = 0; i < triggers.length; i++) {
    ScriptApp.deleteTrigger(triggers[i]);
  }
  // Reset failure counter
  try { PropertiesService.getScriptProperties().deleteProperty('CONN_FAIL_COUNT'); } catch(e) {}
}

function checkConnectionActive() {
  const props = PropertiesService.getScriptProperties();
  const payload = {
    secret: CONFIG.SECRET,
    website_id: CONFIG.WEBSITE_ID,
    phone: "check",
    message: "check"
  };
  const options = {
    method: "post",
    contentType: "application/json",
    payload: JSON.stringify(payload),
    muteHttpExceptions: true
  };
  try {
    const response = UrlFetchApp.fetch(CONFIG.API_URL, options);
    const code = response.getResponseCode();
    if (code === 403) {
      // Server explicitly says disabled — stop immediately
      return false;
    }
    // Success — reset failure counter
    props.deleteProperty('CONN_FAIL_COUNT');
    return true;
  } catch(e) {
    // Network error — count consecutive failures
    const failCount = parseInt(props.getProperty('CONN_FAIL_COUNT') || '0', 10) + 1;
    props.setProperty('CONN_FAIL_COUNT', String(failCount));
    // After 3 consecutive failures (3 minutes), assume disabled
    if (failCount >= 3) {
      return false;
    }
    // Within tolerance — keep running
    return true;
  }
}

function onOpen() {
  const ui = SpreadsheetApp.getUi();
  ui.createMenu('Chatbaze Connector')
    .addItem('Aktifkan Pengingat (Start)', 'setupReminderTrigger')
    .addItem('Matikan Pengingat (Stop)', 'deleteTriggers')
    .addToUi();
}

function setupReminderTrigger() {
  const ui = SpreadsheetApp.getUi();
  
  // Tes ping koneksi ke Chatbaze
  const payload = {
    secret: CONFIG.SECRET,
    website_id: CONFIG.WEBSITE_ID,
    phone: "PING",
    message: "PING",
    name: "System Setup"
  };
  const options = {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify(payload),
    muteHttpExceptions: true
  };
  
  try {
    const response = UrlFetchApp.fetch(CONFIG.API_URL, options);
    const code = response.getResponseCode();
    
    if (code === 403) {
      ui.alert('Gagal Mengaktifkan', 'Konektor Google Sheets belum diaktifkan di dashboard Chatbaze! Silakan aktifkan konektor terlebih dahulu di dashboard Chatbaze sebelum menyalakan pengingat.', ui.ButtonSet.OK);
      return;
    }
    
    // Hapus trigger lama agar tidak duplikat
    deleteTriggers();
    
    // Buat trigger waktu setiap menit
    ScriptApp.newTrigger('runMedicationReminder')
      .timeBased()
      .everyMinutes(1)
      .create();
      
    ui.alert('Berhasil Diaktifkan', 'Pengingat otomatis berhasil dinyalakan! Script akan berjalan setiap menit untuk mengirimkan pengingat jadwal obat pasien.', ui.ButtonSet.OK);
  } catch(e) {
    ui.alert('Kesalahan Koneksi', 'Gagal menghubungi server Chatbaze: ' + e.message, ui.ButtonSet.OK);
  }
}

function resetDailyStatus() {
  const ss = SpreadsheetApp.getActiveSpreadsheet();
  const sheets = ss.getSheets();
  sheets.forEach(sheet => {
    const lastRow = sheet.getLastRow();
    if (lastRow > 1) {
      sheet.getRange(2, 6, lastRow - 1, 1).clearContent();
    }
  });
}`;

  const copyToClipboard = (txt: string, fn: (v: boolean) => void) => {
    navigator.clipboard.writeText(txt);
    fn(true);
    toast({ title: "Teks disalin!" });
    setTimeout(() => fn(false), 2000);
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in pb-20 p-4 md:p-0">
      <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="flex items-center gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm">
            <FileSpreadsheet className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Panduan Google Sheets Connector</h1>
            <p className="text-muted-foreground mt-1 text-pretty">Hubungkan data Anda dan aktifkan otomatisasi WhatsApp dalam hitungan menit.</p>
          </div>
        </div>
      </div>

      {/* Step 1: Sharing */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
          <Lock className="h-6 w-6 text-primary" /> Langkah 1: Berikan Akses ke Chatbaze
        </h2>
        <Card className="border-primary/20 shadow-md bg-gradient-to-br from-primary/[0.02] to-transparent">
          <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
            <p>Pastikan Chatbaze memiliki izin untuk membaca data di spreadsheet Anda.</p>
            <ol className="list-decimal pl-5 space-y-3">
              <li>Klik tombol <strong>Share</strong> di Google Sheets Anda.</li>
              <li>Masukkan email <b>Service Account</b> berikut sebagai <strong>Viewer</strong>:
                <div className="mt-2 p-2 bg-muted rounded-lg border border-dashed flex items-center justify-between group overflow-hidden shadow-sm">
                  <code className="text-[10px] sm:text-xs font-mono truncate mr-2">{serviceAccountEmail}</code>
                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(serviceAccountEmail, setCopied)} className="h-8 w-8">
                    {copied ? <CheckCheck className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Step 2: Automation Scenarios */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
          <Bell className="h-6 w-6 text-primary" /> Langkah 2: Pilih Skenario & Template
        </h2>
        
        {/* Scenario 1: Printing */}
        <Card className="border-primary/20 shadow-sm overflow-hidden">
          <CardHeader className="bg-slate-50 border-b py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Badge className="bg-blue-500">Skenario 1</Badge> Notifikasi Layanan Percetakan
                </CardTitle>
                <CardDescription>Kirim WA otomatis saat status pesanan diubah menjadi "Selesai".</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100 gap-1.5 w-fit"
                onClick={() => window.open(templateLink, '_blank')}
              >
                <Download className="h-3.5 w-3.5" />
                Template Percetakan
              </Button>
            </div>
          </CardHeader>
          <div className="bg-slate-900 p-3 flex items-center justify-between border-b border-white/10">
            <span className="text-[10px] font-mono text-slate-400">notif_percetakan.js</span>
            <Button variant="secondary" size="sm" className="h-7 text-[10px] bg-white/10 text-white hover:bg-white/20 border-none" onClick={() => copyToClipboard(fullAppsScript, setScriptCopied)}>
               {scriptCopied ? "Berhasil Disalin" : "Salin Script Percetakan"}
            </Button>
          </div>
          <CardContent className="pt-4 space-y-4 text-sm">
            <p className="text-xs text-muted-foreground italic">Gunakan template di atas, pasang script di Apps Script, dan aktifkan Trigger <b>On Edit</b>.</p>
          </CardContent>
        </Card>

        {/* Scenario 2: Medication Reminder */}
        <Card className="border-emerald-200 shadow-sm overflow-hidden mt-6">
          <CardHeader className="bg-emerald-50 border-b py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Badge className="bg-emerald-500">Skenario 2</Badge> Pengingat Minum Obat (Dokter)
                </CardTitle>
                <CardDescription>Kirim WA otomatis sesuai jadwal jam minum obat pasien setiap hari.</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 gap-1.5 w-fit"
                onClick={() => window.open(medicalTemplateLink, '_blank')}
              >
                <Download className="h-3.5 w-3.5" />
                Template Medis
              </Button>
            </div>
          </CardHeader>
          <div className="bg-slate-900 p-3 flex items-center justify-between border-b border-white/10">
            <span className="text-[10px] font-mono text-slate-400">reminder_obat.js</span>
            <Button variant="secondary" size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white border-none" onClick={() => copyToClipboard(remiAppsScript, setRemiCopied)}>
               {remiCopied ? "Berhasil Disalin" : "Salin Script Reminder"}
            </Button>
          </div>
          <CardContent className="pt-4 space-y-4 text-sm bg-emerald-50/20">
            <p className="text-xs text-muted-foreground italic">Gunakan template di atas, pasang script di Apps Script, dan aktifkan Trigger <b>Time-driven (setiap menit)</b>.</p>
            <div className="bg-white p-3 rounded-lg border border-emerald-100 text-[11px] space-y-1">
               <p className="font-bold text-emerald-700 uppercase tracking-wider">Struktur Kolom:</p>
               <p>A: Baris | B: Nama Pasien | C: No WA | D: Nama Obat | E: Jam | F: Status | G: Keterangan</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Step 3: Final Connection */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
          <Link2 className="h-6 w-6 text-primary" /> Langkah 3: Daftarkan Konektor
        </h2>
        <Card className="border-primary/10 shadow-sm">
          <CardContent className="pt-6 space-y-4 text-sm text-muted-foreground">
            <ol className="list-decimal pl-6 space-y-3">
              <li>Copy <b>Spreadsheet ID</b> dari URL file Anda.</li>
              <li>Masuk ke <strong className="text-foreground">Knowledge Base &gt; Data Connector</strong>.</li>
              <li>Pilih <strong className="text-foreground">Google Sheets</strong>, tempelkan ID, dan Simpan.</li>
            </ol>
          </CardContent>
        </Card>
      </div>

      <div className="pt-8 text-center">
        <p className="text-[10px] text-muted-foreground italic">Butuh bantuan? Tim support kami siap membantu via chat.</p>
      </div>
    </div>
  );
};

export default GoogleSheetsGuide;
